import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { initSentry, Sentry } from '~/sentry';
import { AppErrorBoundary } from '~/components/app-error-boundary';
import { ConfirmDialogProvider } from '~/components/confirm-dialog';
import { ThemedToaster } from '~/components/themed-toaster';
import { TooltipProvider } from '~/components/ui/tooltip';
import { TooltipsPreferenceProvider } from '~/features/settings/tooltips-preference-provider';
import { ComputeBlocksFlagProvider } from '~/features/limits/compute-blocks-display';
import { getQueryClient } from '~/lib/query-client';
import { maybeRedirectToMobile } from '~/lib/mobile-redirect';
import { routes } from '~/routes';
// Self-hosted fonts (GDPR — no request to Google's servers). Geist variable
// covers the whole 100–900 weight range in one woff2; IBM Plex Mono ships the
// two weights the UI uses. Imported before app.css so the @font-face rules are
// registered ahead of first paint.
import '@fontsource-variable/geist/wght.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@xterm/xterm/css/xterm.css';
import './app.css';

initSentry();

// A phone visiting the desktop SPA should never paint the desktop shell
// first. `location.replace()` does NOT halt script execution, so without the
// guard below the router would still mount, `AppLayout` would still render,
// and the desktop app's boot queries (auth, onboarding, session resolve, SSE
// connect) would still fire in the moment before the navigation lands. The
// returned boolean lets us skip mounting anything at all.
const redirectedToMobile = maybeRedirectToMobile();

/**
 * Stale-build self-heal (FRONT-322). After a deploy, a tab loaded against
 * the previous build lazy-imports route chunks by their OLD hashed names;
 * the new pod 404s them and Vite emits `vite:preloadError` instead of
 * rendering the route. One automatic reload fetches the current
 * index.html → current chunk names.
 *
 * Loop guard: a timestamp in sessionStorage, NOT a flag cleared on boot —
 * a boot-time clear would re-arm before the next failure and reload
 * forever when an asset is missing for a non-deploy reason. Within the
 * cooldown a second failure falls through to the error boundary instead.
 */
const RELOAD_GUARD = 'eddy:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;
window.addEventListener('vite:preloadError', (event) => {
  const last = Number(sessionStorage.getItem(RELOAD_GUARD) ?? 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // just retried — surface the error
  sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
  event.preventDefault(); // suppress the error boundary; the reload supersedes it
  window.location.reload();
});

if (!redirectedToMobile) {
  const router = createBrowserRouter(routes);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<div style={{padding: 24}}>Something broke. Refresh.</div>}>
        <AppErrorBoundary>
          <QueryClientProvider client={getQueryClient()}>
            {/* FRONT-2267: inside QueryClientProvider (it reads the person's
                saved switch) and outside TooltipProvider, so every hint in
                the tree — Radix's and ours — is under one answer. */}
            <TooltipsPreferenceProvider>
              {/* FRONT-2670: the org's compute-blocks display switch, read once
                  for every surface (desktop and phone) below it. */}
              <ComputeBlocksFlagProvider>
                <TooltipProvider>
                  <ConfirmDialogProvider>
                    <RouterProvider router={router} />
                    <ThemedToaster />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </ConfirmDialogProvider>
                </TooltipProvider>
              </ComputeBlocksFlagProvider>
            </TooltipsPreferenceProvider>
          </QueryClientProvider>
        </AppErrorBoundary>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
}
